import { type ReactElement, type ReactNode, Fragment } from 'react';
import { StatusMetricCardsRow } from '@/features/metrics/components/StatusMetricCardsRow';
import { chartLoadErrorBannerSx } from '@/features/shared/components/chartPanels/chartLoadErrorBannerSx';
import { NativeTablePanelTabs } from '@/features/shared/components/tableSystem/NativeTablePanelTabs';
import { tablePanelSurfaceSx } from '@/features/shared/components/tableSystem/tablePanelSurfaceSx';
import { getChartTitle } from '@/shared/copy/charts';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { getErrorCopy } from '@/shared/copy/errors';
import {
  metricThresholdGroupKeys,
  type MetricThresholdGroupKey,
} from '@/shared/utils/metricsThresholds';
import { s, typographyTokens } from '@/theme/tokens';
import { uiTokens, type UiTone } from '@/theme/uiTokens';
import { Panel, StatusBanner } from '@/ui/composites';
import {
  ChartEmptyState,
  CHART_EMPTY_STATE_FILTER_HINT,
} from '@/ui/composites/charts/ChartEmptyState';
import { ListPageTemplate } from '@/ui/composites/scaffolds';
import { useDeferredAsyncViewState } from '@/ui/composites/useDeferredAsyncViewState';
import { Stack } from '@/ui/layout/Stack';
import {
  BarChart,
  Box,
  Button,
  LineChart,
  LineChartSkeleton,
  StackedBarChartSkeleton,
  Text,
} from '@/ui/primitives';
import { StackedBarChart, type StackedBarChartDatum } from '@/ui/primitives/StackedBarChart';
import { Tab, Tabs } from '@/ui/primitives/Tabs';
import { SearchTagsDateHeaderFiltersNext } from './SearchTagsDateHeaderFiltersNext';
import type { MetricValueItem } from '@/features/metrics/components/metricValueItems';
import type { MetricCardItem } from '@/features/metrics/components/statusMetricCards';
import type { QualityRange } from '@/features/quality/hooks';

export type EntityChartTab = 'status' | 'pass-rate' | 'frequency' | 'duration';

export type EntityStackedBarSeries = {
  key: string;
  label: string;
  color?: string;
  colorKey?: UiTone;
};

export type EntityPageContentProps<TActiveTab extends string> = {
  range: QualityRange;
  chartTab: EntityChartTab;
  activeTab: TActiveTab;
  entityTabValue: TActiveTab;
  searchValue: string;
  pickerStartDay: string | null;
  pickerEndDay: string | null;
  hasCustomDates: boolean;
  isLatestMode: boolean;
  metricCards: MetricCardItem[];
  selectedStatuses: string[];
  statusOptions?: Array<{ value: string; label: string }>;
  selectedTags: string[];
  tagOptions: Array<{ value: string; label: string }>;
  tagOptionsLoading: boolean;
  selectedProjects?: string[];
  onProjectsChange?: (nextProjects: string[]) => void;
  projectOptions?: Array<{ value: string; label: string }>;
  metricValueItems: MetricValueItem[];
  visibleMetricKeys?: MetricThresholdGroupKey[];
  chartHeight: number;
  showMetricCardsSkeleton: boolean;
  showChartSkeleton: boolean;
  showChartErrorState: boolean;
  suppressChartAnimation: boolean;
  showLineChartPoints: boolean;
  filteredStackedChartData: {
    data: StackedBarChartDatum[];
    series: EntityStackedBarSeries[];
  } | null;
  filteredPassRateChartData: Array<Record<string, string | number | null>> | null;
  filteredFrequencyChartData: Array<Record<string, string | number | null>> | null;
  filteredDurationChartData: Array<Record<string, string | number | null>> | null;
  deltasEnabled: boolean;
  onRangeChange: (nextRange: QualityRange) => void;
  onSearchChange: (value: string) => void;
  onChartTabChange: (nextTab: EntityChartTab) => void;
  onActiveTabChange: (nextTab: TActiveTab) => void;
  onStatusToggle: (status: string) => void;
  onStatusesChange?: (nextStatuses: string[]) => void;
  onTagsChange: (nextTags: string[]) => void;
  onChartSelect: (dataIndex: number, seriesKey?: string) => void;
  onSelectedStartDayChange: (nextDay: string | null) => void;
  onSelectedEndDayChange: (nextDay: string | null) => void;
  showChartDateClear: boolean;
  onChartDateClear: () => void;
  titleActions?: ReactNode;
  footer?: ReactNode;
  title: string;
  addButtonLabel: string;
  searchAriaLabel: string;
  chartTabsAriaLabel: string;
  pageTabsAriaLabel: string;
  entityTabLabel: string;
  renderEntityTable: (showTableDeltas: boolean, noRowsText?: string) => ReactElement;
  renderExecutionsTable: (noRowsText?: string) => ReactElement;
  chartEmptyStateTitle?: string;
  entityTableNoRowsText?: string;
  executionTableNoRowsText?: string;
  contentAbove?: ReactNode;
  contentBelow?: ReactNode;
  contentOverride?: ReactNode;
  hideHeader?: boolean;
};

const hasVisibleChartValue = (
  data: Array<Record<string, string | number | null>> | null,
  key: string,
): boolean =>
  Boolean(
    data?.some((row) => {
      const value = row[key];
      return typeof value === 'number' && Number.isFinite(value) && value > 0;
    }),
  );

const hasVisibleStackedChartValue = (
  data: Array<Record<string, string | number>>,
  series: EntityStackedBarSeries[],
): boolean =>
  data.some((row) =>
    series.some((seriesItem) => {
      const value = row[seriesItem.key];
      return typeof value === 'number' && Number.isFinite(value) && value > 0;
    }),
  );

const resolveMetricCardCount = (card: MetricCardItem | undefined): number | null => {
  if (card?.isMissing === true) return 0;
  const count = card?.count;
  if (typeof count === 'number' && Number.isFinite(count)) return count;
  if (typeof count !== 'string') return null;
  const parsedCount = Number.parseInt(count.replaceAll(',', ''), 10);
  return Number.isFinite(parsedCount) ? parsedCount : null;
};

const resolveMetricCardSecondaryLabel = (card: MetricCardItem | undefined): string | null =>
  typeof card?.secondaryValue === 'string' && card.secondaryValue.trim().length > 0
    ? card.secondaryValue.trim()
    : null;

export const EntityPageContent = <TActiveTab extends string>({
  range,
  chartTab,
  activeTab,
  entityTabValue,
  searchValue,
  pickerStartDay,
  pickerEndDay,
  hasCustomDates,
  isLatestMode,
  metricCards,
  selectedStatuses,
  statusOptions,
  selectedTags,
  tagOptions,
  tagOptionsLoading,
  selectedProjects,
  onProjectsChange,
  projectOptions,
  metricValueItems: _metricValueItems,
  visibleMetricKeys = metricThresholdGroupKeys,
  chartHeight,
  showMetricCardsSkeleton,
  showChartSkeleton,
  showChartErrorState,
  suppressChartAnimation,
  showLineChartPoints,
  filteredStackedChartData,
  filteredPassRateChartData,
  filteredFrequencyChartData,
  filteredDurationChartData,
  deltasEnabled,
  onRangeChange,
  onSearchChange,
  onChartTabChange,
  onActiveTabChange,
  onStatusToggle,
  onStatusesChange,
  onTagsChange,
  onChartSelect,
  onSelectedStartDayChange,
  onSelectedEndDayChange,
  showChartDateClear,
  onChartDateClear,
  titleActions,
  footer,
  title,
  addButtonLabel,
  searchAriaLabel,
  chartTabsAriaLabel,
  pageTabsAriaLabel,
  entityTabLabel,
  renderEntityTable,
  renderExecutionsTable,
  chartEmptyStateTitle = getEmptyStateMessage('data.noDataFound'),
  entityTableNoRowsText,
  executionTableNoRowsText,
  contentAbove,
  contentBelow,
  contentOverride,
  hideHeader = false,
}: EntityPageContentProps<TActiveTab>): ReactElement => {
  const chartViewState = useDeferredAsyncViewState({
    loading: showChartSkeleton,
    hasError: showChartErrorState,
  });
  const tabsContentGap = 'md' as const;
  const isDeltaToggleDisabled = isLatestMode || range === 'all';
  const showTableDeltas = deltasEnabled && !isDeltaToggleDisabled;
  const hasStatusData = filteredStackedChartData
    ? hasVisibleStackedChartValue(filteredStackedChartData.data, filteredStackedChartData.series)
    : false;
  const hasPassRateData = hasVisibleChartValue(filteredPassRateChartData, 'passRate');
  const hasFrequencyData = hasVisibleChartValue(filteredFrequencyChartData, 'frequency');
  const hasDurationData = hasVisibleChartValue(filteredDurationChartData, 'duration');
  const visibleMetricKeySet = new Set(visibleMetricKeys);
  const showPassRateChart = visibleMetricKeySet.has('passRate');
  const showFrequencyChart = visibleMetricKeySet.has('frequency');
  const showDurationChart = visibleMetricKeySet.has('avgDuration');
  const resolvedChartTab =
    (chartTab === 'pass-rate' && !showPassRateChart) ||
    (chartTab === 'frequency' && !showFrequencyChart) ||
    (chartTab === 'duration' && !showDurationChart)
      ? 'status'
      : chartTab;
  const toolbarSummaryCount = resolveMetricCardCount(
    metricCards.find((metricCard) => metricCard.id === 'total'),
  );
  const toolbarSummaryRelatedEntityLabel = resolveMetricCardSecondaryLabel(
    metricCards.find((metricCard) => metricCard.id === 'total'),
  );
  const summary = (
    <StatusMetricCardsRow
      cards={showMetricCardsSkeleton ? [] : metricCards}
      loading={showMetricCardsSkeleton}
      selectedIds={selectedStatuses}
      onToggle={(status) => {
        if (status === 'total') return;
        onStatusToggle(status);
      }}
    />
  );
  const charts = (
    <Panel padding="md" gap="none" contentGap="lg" sx={{ backgroundColor: 'background.paper' }}>
      <Stack gap={tabsContentGap}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" align="center" justify="space-between" sx={{ minWidth: 0 }}>
            <Tabs
              value={resolvedChartTab}
              onChange={(_, next) => {
                onChartTabChange(next as EntityChartTab);
              }}
              aria-label={chartTabsAriaLabel}
            >
              <Tab label={getChartTitle('status')} value="status" />
              {showPassRateChart ? (
                <Tab label={getChartTitle('passRate')} value="pass-rate" />
              ) : null}
              {showFrequencyChart ? (
                <Tab label={getChartTitle('frequency')} value="frequency" />
              ) : null}
              {showDurationChart ? (
                <Tab label={getChartTitle('duration')} value="duration" />
              ) : null}
            </Tabs>
            {showChartDateClear ? (
              <Box
                component="button"
                type="button"
                onClick={() => {
                  onChartDateClear();
                }}
                sx={{
                  color: 'primary.main',
                  cursor: 'pointer',
                  fontSize: typographyTokens.captionSmall.fontSize,
                  mr: s('md'),
                  background: 'none',
                  border: 'none',
                  padding: s('none'),
                  userSelect: 'none',
                  font: 'inherit',
                  lineHeight: 'inherit',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                RESET
              </Box>
            ) : null}
          </Stack>
        </Box>
        {chartViewState.showPlaceholder ? (
          <Box aria-hidden="true" sx={{ height: chartHeight }} />
        ) : chartViewState.showSkeleton ? (
          resolvedChartTab === 'frequency' || resolvedChartTab === 'duration' ? (
            <LineChartSkeleton height={chartHeight} />
          ) : (
            <StackedBarChartSkeleton height={chartHeight} />
          )
        ) : chartViewState.showError ? (
          <Box
            sx={{
              height: chartHeight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <StatusBanner
              tone="red"
              variant="outlined"
              message={getErrorCopy('banner.chartLoadFailed')}
              sx={chartLoadErrorBannerSx}
            />
          </Box>
        ) : resolvedChartTab === 'status' ? (
          filteredStackedChartData ? (
            hasStatusData ? (
              <StackedBarChart
                data={filteredStackedChartData.data}
                series={filteredStackedChartData.series}
                height={chartHeight}
                showAllXAxisLabels
                showHorizontalGrid
                skipAnimation={suppressChartAnimation}
                onSelectBar={onChartSelect}
              />
            ) : (
              <Box
                sx={{
                  height: chartHeight,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ChartEmptyState
                  title={chartEmptyStateTitle}
                  body={CHART_EMPTY_STATE_FILTER_HINT}
                />
              </Box>
            )
          ) : (
            <Box sx={{ height: chartHeight }} />
          )
        ) : resolvedChartTab === 'pass-rate' && showPassRateChart ? (
          filteredPassRateChartData ? (
            hasPassRateData ? (
              <BarChart
                data={filteredPassRateChartData}
                series={[{ key: 'passRate', label: '', color: uiTokens.statusColors.green }]}
                height={chartHeight}
                showAllXAxisLabels
                showLegend={false}
                onSelectBar={(dataIndex) => {
                  onChartSelect(dataIndex);
                }}
              />
            ) : (
              <Box
                sx={{
                  height: chartHeight,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ChartEmptyState
                  title={chartEmptyStateTitle}
                  body={CHART_EMPTY_STATE_FILTER_HINT}
                />
              </Box>
            )
          ) : (
            <Box sx={{ height: chartHeight }} />
          )
        ) : resolvedChartTab === 'frequency' && showFrequencyChart ? (
          filteredFrequencyChartData ? (
            hasFrequencyData ? (
              <LineChart
                data={filteredFrequencyChartData}
                series={[{ key: 'frequency', label: '', color: 'var(--color-pink)' }]}
                height={chartHeight}
                showAllXAxisLabels
                showLegend={false}
                onSelectBar={(dataIndex) => {
                  onChartSelect(dataIndex);
                }}
                showPoints={showLineChartPoints || filteredFrequencyChartData.length === 1}
              />
            ) : (
              <Box
                sx={{
                  height: chartHeight,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ChartEmptyState
                  title={chartEmptyStateTitle}
                  body={CHART_EMPTY_STATE_FILTER_HINT}
                />
              </Box>
            )
          ) : (
            <Box sx={{ height: chartHeight }} />
          )
        ) : resolvedChartTab === 'duration' && showDurationChart && filteredDurationChartData ? (
          hasDurationData ? (
            <LineChart
              data={filteredDurationChartData}
              series={[{ key: 'duration', label: '' }]}
              height={chartHeight}
              showAllXAxisLabels
              showLegend={false}
              onSelectBar={(dataIndex) => {
                onChartSelect(dataIndex);
              }}
              showPoints={showLineChartPoints || filteredDurationChartData.length === 1}
            />
          ) : (
            <Box
              sx={{
                height: chartHeight,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ChartEmptyState title={chartEmptyStateTitle} body={CHART_EMPTY_STATE_FILTER_HINT} />
            </Box>
          )
        ) : (
          <Box sx={{ height: chartHeight }} />
        )}
      </Stack>
    </Panel>
  );
  const content = contentOverride ?? (
    <Panel padding="md" gap="none" contentGap="md" sx={tablePanelSurfaceSx}>
      <Stack gap={tabsContentGap}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" align="center" sx={{ minWidth: 0 }}>
            <NativeTablePanelTabs
              value={activeTab}
              onChange={(next) => {
                onActiveTabChange(next);
              }}
              ariaLabel={pageTabsAriaLabel}
              tabs={[
                { label: entityTabLabel, value: entityTabValue },
                { label: 'Runs', value: 'executions' as TActiveTab },
              ]}
            />
          </Stack>
        </Box>
        {activeTab === entityTabValue
          ? renderEntityTable(showTableDeltas, entityTableNoRowsText)
          : renderExecutionsTable(executionTableNoRowsText)}
      </Stack>
    </Panel>
  );
  const contentParts = [
    { key: 'content-above', node: contentAbove },
    { key: 'content', node: content },
    { key: 'content-below', node: contentBelow },
  ].filter((value): value is { key: string; node: ReactNode } => value.node != null);
  const resolvedContent =
    contentParts.length === 1 ? (
      contentParts[0].node
    ) : (
      <Stack gap="md">
        {contentParts.map((contentPart) => (
          <Fragment key={contentPart.key}>{contentPart.node}</Fragment>
        ))}
      </Stack>
    );

  return (
    <ListPageTemplate
      headerActionsMode="separate"
      title={
        <Text role="pageTitle" component="h1">
          {title}
        </Text>
      }
      titleActions={titleActions === undefined ? <Button>{addButtonLabel}</Button> : titleActions}
      headerActions={
        contentOverride ? null : (
          <Stack gap="md">
            <SearchTagsDateHeaderFiltersNext
              searchValue={searchValue}
              onSearchChange={onSearchChange}
              searchAriaLabel={searchAriaLabel}
              selectedStatuses={selectedStatuses}
              onStatusesChange={onStatusesChange}
              statusOptions={statusOptions}
              selectedTags={selectedTags}
              onTagsChange={onTagsChange}
              tagOptions={tagOptions}
              tagOptionsLoading={tagOptionsLoading}
              selectedProjects={selectedProjects}
              onProjectsChange={onProjectsChange}
              projectOptions={projectOptions}
              range={range}
              onRangeChange={onRangeChange}
              pickerStartDay={pickerStartDay}
              pickerEndDay={pickerEndDay}
              hasCustomDates={hasCustomDates}
              isLatestMode={isLatestMode}
              onSelectedStartDayChange={onSelectedStartDayChange}
              onSelectedEndDayChange={onSelectedEndDayChange}
              includeLatest
              summaryCount={toolbarSummaryCount}
              summaryStartDay={pickerStartDay}
              summaryEndDay={pickerEndDay}
              summaryRelatedEntityLabel={toolbarSummaryRelatedEntityLabel}
              summaryEmptyText={`${chartEmptyStateTitle} ${CHART_EMPTY_STATE_FILTER_HINT}`}
              summaryLoading={showMetricCardsSkeleton}
            />
          </Stack>
        )
      }
      summary={contentOverride ? null : summary}
      charts={contentOverride ? null : charts}
      content={resolvedContent}
      footer={footer}
      hideHeader={hideHeader}
    />
  );
};
